// cspell:disable
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { memo, useMemo } from "react";

export const dhlDirectIntegrationSettingsForm = groupInput({
  pickupId: input<string>(),
  distributionCenter: input<string>(),
  useDefaultForAllLocations: input<boolean>(),
});

export type DhlDirectIntegrationSettingsForm = InputSchema.Input<
  typeof dhlDirectIntegrationSettingsForm
>;

export type DhlDirectIntegrationSettingsValue = InputSchema.Value<
  typeof dhlDirectIntegrationSettingsForm
>;

export const dhlDirectIntegrationSettingsDefault: DhlDirectIntegrationSettingsValue =
  { pickupId: "", distributionCenter: "", useDefaultForAllLocations: false };

// https://developer.dhl.com/api-reference/references-dhl-ecommerce-americas#get-started-section/tax-id-types
const DHL_DISTRIBUTION_CENTERS = [
  { id: "USATL1", label: "Mableton, GA" },
  { id: "USBOS1", label: "Westborough, MA" },
  { id: "USBWI1", label: "Manassas, VA" },
  { id: "USCAK1", label: "Stow, OH" },
  { id: "USCVG1", label: "Hebron, KY" },
  { id: "USDEN1", label: "Denver, CO" },
  { id: "USDFW1", label: "Irving, TX" },
  { id: "USEWR1", label: "Avenel, NJ" },
  { id: "USIAH1", label: "Houston, TX" },
  { id: "USLAX1", label: "Whittier, CA" },
  { id: "USMCO1", label: "Orlando, FL" },
  { id: "USMCI1", label: "Kansas City, MO" },
  { id: "USORD1", label: "Melrose Park, IL" },
  { id: "USPHX1", label: "Phoenix, AZ" },
  { id: "USRDU1", label: "Concord, NC" },
  { id: "USSEA1", label: "Sumner, WA" },
  { id: "USSFO1", label: "Union City, CA" },
  { id: "USSLC1", label: "West Jordan, UT" },
  { id: "CNYYZ1", label: "Toronto, CA" },
];

export const DhlDirectIntegrationSettings = memo(
  function DhlDirectIntegrationSettings({
    input,
  }: {
    input: DhlDirectIntegrationSettingsForm;
  }) {
    const { inputs } = input;

    const distributionCenterOptions: RedoListItem<string>[] = useMemo(
      () =>
        DHL_DISTRIBUTION_CENTERS.map((dc) => ({
          id: dc.id,
          type: "text" as const,
          text: dc.label + " (" + dc.id + ")",
          value: dc.id,
        })),
      [],
    );

    return (
      <Flex dir="column" gap="md" mt="md">
        <Flex dir="column" gap="xs">
          <Text fontSize="sm" fontWeight="semibold">
            DHL Direct Integration Settings
          </Text>
        </Flex>

        <Flex dir="column" gap="sm" w="sm">
          <RedoToggle
            label="Use this pickup ID and distribution center for all locations"
            setValue={inputs.useDefaultForAllLocations.setValue}
            value={inputs.useDefaultForAllLocations.value}
          />
          <Text fontSize="xs" fontWeight="medium">
            Pickup ID
          </Text>
          <RedoTextInput
            placeholder="Enter pickup ID"
            setValue={inputs.pickupId.setValue}
            value={inputs.pickupId.value}
          />

          <Text fontSize="xs" fontWeight="medium" mt="sm">
            Distribution Center
          </Text>
          <RedoSingleSelectDropdownInput<string>
            options={distributionCenterOptions}
            optionSelected={({ item }) => {
              inputs.distributionCenter.setValue(item.value);
            }}
            placeholder="Select distribution center"
            selectedItem={distributionCenterOptions.find(
              (o) => o.value === inputs.distributionCenter.value,
            )}
            size="xs"
          />
        </Flex>
      </Flex>
    );
  },
);
